import React, { useContext, useEffect } from "react";
import styled, { useTheme } from "styled-components";
import { secured } from "react-abac";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, CRUDActionEnum, EventResponseStatusEnum, EventSurveyStatusEnum, IntegrationTypeEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { Avatar, Button, Card, Dropdown, Grid, Icon, InputToggle, Picture, Spinner, Text, Section, Alert, Modal } from "~/components-shared";
import CoreContext from "~/lib/context";
import { useDispatch, useSelector } from "react-redux";
import eventSelectors from "~/store/events/selectors";
import eventsActionCreators from "~/store/events/actions";
import EventUpdateModal from "~/components/EventUpdateModal";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import EventDeleteModal from "~/components/EventDeleteModal";
import lists from "~/lib/lists";
import { resolveDates } from "~/lib/helpers";
import DefaultSideBar from "~/components/DefaultSideBar";
import integrationSelectors from "~/store/integrations/selectors";
import ErrorSection from "~/components/ErrorSection";
import useAbacExt from "~/hooks/abac";
import EventResponseCard from "~/components/EventResponseCard";
import { composeCalendarURIFromEvent } from "~/integrations/google/helpers";
import Markdown from "~/components/Markdown";
const Status = styled.i`
  position: absolute;
  top: 20px;
  left: 20px;
  border-radius: 50%;
  background: ${_ref => {
  let {
    status,
    theme
  } = _ref;
  switch (status) {
    case EventResponseStatusEnum.ACCEPTED:
      return theme.colors.success.main;
    case EventResponseStatusEnum.DECLINED:
      return theme.colors.danger.main;
    default:
      return theme.colors.borders.secondary;
  }
}};
  width: 16px;
  height: 16px;
  border: 2px solid ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.background.primary;
}};
  color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.background.primary;
}};
  font-style: initial;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const StyledDropdown = styled(Dropdown)`
  display: inline-block;
  vertical-align: bottom;
  margin-left: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.spacing.step * 3;
}}px;
`;
const Event = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    t
  } = useTranslation();
  const {
    setSidebar
  } = useContext(CoreContext);
  const {
    userHasOneOf
  } = useAbacExt();
  const history = useHistory();
  const {
    eventId
  } = useParams();
  const status = useSelector(eventSelectors.getStatusByEvent(eventId));
  const action = useSelector(eventSelectors.getAction);
  const error = useSelector(eventSelectors.getErrorByEvent(eventId));
  const event = useSelector(eventSelectors.getEvent(eventId));
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  useEffect(() => {
    setSidebar(/*#__PURE__*/React.createElement(DefaultSideBar, null));
  }, [setSidebar]);
  useEffect(() => {
    if (!event) {
      dispatch(eventsActionCreators.fetchOneStarted(eventId));
    }
  }, [dispatch, eventId]);
  if (!event && status === StatusStateEnum.FAILED) {
    return /*#__PURE__*/React.createElement(ErrorSection, {
      error: error
    });
  }
  if (!event) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }
  const handleAddToCalendar = () => {
    window.open(composeCalendarURIFromEvent(event), "_blank");
  };
  const handleJoinVideoCall = () => {
    window.open(event.videoLink, "_blank");
  };
  const handleViewCommunity = id => {
    history.push(RoutesEnum.COMMUNITY.replace(":communityId", id));
  };
  const handleViewSurvey = id => {
    history.push(RoutesEnum.EVENT_SURVEY.replace(":eventSurveyId", id));
  };
  const handleViewMember = id => {
    history.push(RoutesEnum.MEMBER.replace(":memberId", id));
  };
  const handleUpdate = () => {
    dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.UPDATE, eventId));
  };
  const handleDelete = () => {
    dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.DELETE, eventId));
  };
  return /*#__PURE__*/React.createElement(Section, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    align: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`,
    color: lists.events.types.colors(theme)[event.type]
  }, lists.events.types.names[event.type]), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    rounded: true,
    display: "inline-block",
    onClick: handleAddToCalendar
  }, t("Event.addToCalendar")), event.videoLink && /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    rounded: true,
    display: "inline-block",
    onClick: handleJoinVideoCall,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "videocam",
      s: {
        mr: 2
      }
    })
  }, t("Event.joinVideoCall")), userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event.createdBy.id) && /*#__PURE__*/React.createElement(StyledDropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      scale: 5,
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref5 => {
    let {
      isOpen,
      onItemClick
    } = _ref5;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event?.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleUpdate();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "pencil",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event?.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleDelete();
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 1
      },
      color: "danger"
    }), t("common.actions.delete")));
  }))), /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    className: `${AnimOrderEnum.TWO} ${AnimClassEnum.POP_RIGHT_FADE}`,
    s: {
      mb: 3
    }
  }, event.name), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    color: "textSecondary",
    fontSize: 4,
    lineHeight: 7,
    s: {
      mb: 8
    }
  }, resolveDates(event.dateFrom, event.dateTo)), event.description ? /*#__PURE__*/React.createElement(Text, {
    element: "p",
    s: {
      mb: 5
    },
    fontSize: 4,
    lineHeight: 7
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: event.description
  })) : /*#__PURE__*/React.createElement(Text, {
    s: {
      mb: 5
    },
    display: "inline-block",
    color: "textSecondary"
  }, t("Event.noDescription")), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(EventResponseCard, {
    s: {
      mb: 5,
      px: 5
    },
    eventId: eventId
  })), /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 5
    }
  }, t("EventForm.communities")), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    equal: true,
    s: {
      mb: 5
    },
    gap: 3,
    align: "stretch"
  }, event.communities.length ? event.communities.map(community => /*#__PURE__*/React.createElement(Card, {
    key: community.id,
    onClick: () => handleViewCommunity(community.id),
    s: {
      p: 5
    },
    border: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    template: "4rem 1fr 20px",
    gap: 3,
    align: "center"
  }, /*#__PURE__*/React.createElement(Picture, {
    width: "4rem",
    height: "4rem",
    src: community.picture,
    color: community.color || theme.colors.secondary.main,
    name: community.name
  }), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    display: "block",
    fontSize: 4
  }, community.name), /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    rotate: 90,
    color: theme.colors.secondary.main
  })))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold"
  }, t("Event.allCommunities")))), /*#__PURE__*/React.createElement(Grid, {
    cols: 2
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 5
    }
  }, t("EventForm.attendees")), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "textSecondary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    color: theme.colors.text.secondary,
    scale: 3
  }), " ", event.attendees.length, " ", /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: theme.colors.secondary.main
  }, "(", event.attendees.filter(attendee => attendee.active).length, ")")))), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, event.attendees.length ? /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    equal: true,
    s: {
      mb: 5
    },
    gap: 3,
    align: "stretch"
  }, event.attendees.map(attendee => /*#__PURE__*/React.createElement(Card, {
    key: attendee.id,
    onClick: () => handleViewMember(attendee.id),
    s: {
      p: 5
    },
    border: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    template: "4rem 1fr 20px",
    gap: 3,
    align: "center"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Avatar, {
    user: attendee,
    size: 14
  }), attendee.responseStatus !== EventResponseStatusEnum.NEEDS_ACTION && /*#__PURE__*/React.createElement(Status, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("Event.accepted"),
    status: attendee.responseStatus
  }, attendee.responseStatus === EventResponseStatusEnum.TENTATIVE ? "?" : /*#__PURE__*/React.createElement(Icon, {
    src: attendee.responseStatus === EventResponseStatusEnum.ACCEPTED ? "tick" : "cross",
    scale: 3
  }))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    color: "textSecondary",
    display: "block",
    fontSize: 3,
    lineHeight: 3.5
  }, attendee.role.name), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    display: "block",
    fontSize: 4
  }, attendee.firstName, " ", attendee.lastName, " "), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    display: "block",
    lineHeight: 4
  }, attendee.organizer && `${t("Event.organizer")} · `, attendee.email)), /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    rotate: 90,
    color: theme.colors.secondary.main
  }))))) : /*#__PURE__*/React.createElement(Text, {
    s: {
      mb: 7
    },
    display: "inline-block",
    color: "textSecondary",
    weight: "semiBold"
  }, t("Event.noAttendees"))), /*#__PURE__*/React.createElement(Grid, {
    cols: 2
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("Event.surveys")), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "textSecondary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "document",
    color: theme.colors.text.secondary,
    scale: 3.5
  }), " ", event.eventSurveys.length))), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, event.eventSurveys.length ? /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    equal: true,
    s: {
      mb: 5
    },
    gap: 3
  }, event.eventSurveys.map(survey => /*#__PURE__*/React.createElement(Card, {
    key: survey.id,
    onClick: () => handleViewSurvey(survey.id),
    s: {
      p: 3
    },
    border: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    template: "4rem 1fr 20px",
    gap: 3,
    align: "center"
  }, /*#__PURE__*/React.createElement(Picture, {
    name: "S",
    width: "4rem",
    height: "4rem",
    border: "circled"
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    color: "textSecondary",
    display: "block",
    fontSize: 3,
    lineHeight: 3.5
  }, t("Event.sendVia", {
    target: lists.eventSurveys.target.names[survey.target]
  })), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    display: "block",
    fontSize: 4
  }, t("Event.questions", {
    count: survey.questions.length
  })), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    display: "block",
    lineHeight: 4
  }, survey.status === EventSurveyStatusEnum.SENT ? t("Event.sent") : survey.scheduledAt ? t("Event.scheduledAt", {
    time: moment(survey.scheduledAt).format("ddd, MMM D hh:mma")
  }) : t("Event.notScheduled"))), /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    rotate: 90,
    color: theme.colors.secondary.main
  }))))) : /*#__PURE__*/React.createElement(Text, {
    s: {
      mb: 7
    },
    display: "block",
    color: "textSecondary",
    weight: "semiBold"
  }, t("Event.noSurveys"))), slackIntegration && /*#__PURE__*/React.createElement(InputToggle, {
    label: t("EventForm.shareSlack"),
    value: event.shareInSlack,
    s: {
      mb: 3
    },
    disabled: true
  }), userHasOneOf([AuthoritiesEnum.EVENTS_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(EventUpdateModal, null)), userHasOneOf([AuthoritiesEnum.EVENTS_WRITE]) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(EventDeleteModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.EVENTS_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(Event), () => true);